import React, {
    useEffect, useMemo, useRef, useState
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Copy as CopyIcon, Check, Pencil, Eye, AlertCircle, RotateCcw
} from 'lucide-react';
import {
    EditorView, lineNumbers, highlightActiveLine, Decoration
} from '@codemirror/view';
import {
    EditorState, StateEffect, StateField
} from '@codemirror/state';
import {
    json
} from '@codemirror/lang-json';
import {
    highlightSelectionMatches
} from '@codemirror/search';
import {
    autocompletion
} from '@codemirror/autocomplete';
import {
    codeFolding, foldGutter
} from '@codemirror/language';
import {
    DSButton
} from '../ui';

/**
 * EntityJsonView — canonical-JSON view / editor for any DE Studio entity.
 *
 * Uses a CodeMirror 6 editor (same setup as M77 OpCode JSON mode) so
 * users get line numbers, syntax highlighting, folding, search, and
 * autocomplete in BOTH view and edit modes.
 *
 * View mode (default): readOnly editor, with masked secrets when
 *   sanitize=true.
 * Edit mode (when `onApply` is provided + user clicks Edit): editor
 *   becomes editable, live-validates JSON, surfaces parse errors with
 *   red line highlight, and on Apply calls onApply(parsed).
 *
 * Secret round-trip: in edit mode, the editor shows the RAW unmasked
 * value so the user can edit real secrets. If they don't touch a
 * masked-keyed value (which they wouldn't typically have masked anyway
 * since edit shows raw), nothing changes.
 */

const SECRET_KEY_RE = /^(token|password|apikey|api_key|apiKey|client_secret|secret|bearer_token|access_token|refresh_token)$/i;
const MASK = '********';

function maskSecrets(value) {
    if (Array.isArray(value)) return value.map(maskSecrets);
    if (value && typeof value === 'object') {
        const out = {};
        for (const k of Object.keys(value)) {
            if (SECRET_KEY_RE.test(k) && value[k] !== null && value[k] !== '' && value[k] !== undefined) {
                out[k] = MASK;
            } else {
                out[k] = maskSecrets(value[k]);
            }
        }
        return out;
    }
    return value;
}

function safeStringify(v) {
    try {
        return JSON.stringify(v, null, 2);
    } catch (err) {
        return `// Could not stringify: ${err?.message || String(err)}`;
    }
}

// Error-line highlight machinery — same pattern as M77 EditorPanel.
const setErrorLineEffect = StateEffect.define();
const errorLineField = StateField.define({
    create() { return Decoration.none; },
    update(deco, tr) {
        deco = deco.map(tr.changes);
        for (const e of tr.effects) {
            if (e.is(setErrorLineEffect)) {
                if (e.value == null) return Decoration.none;
                try {
                    const line = tr.state.doc.line(e.value);
                    return Decoration.set([
                        Decoration.line({
                            class: 'cm-errorLine'
                        }).range(line.from)
                    ]);
                } catch {
                    return Decoration.none;
                }
            }
        }
        return deco;
    },
    provide: (f) => EditorView.decorations.from(f)
});
const errorLineTheme = EditorView.theme({
    '.cm-errorLine': {
        backgroundColor: 'rgba(244, 63, 94, 0.12)'
    }
});

const editorBaseTheme = EditorView.theme({
    '&': {
        fontSize: '13px',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        minHeight: '420px',
        height: '420px'
    },
    '.cm-content': {
        padding: '12px',
        minHeight: '420px'
    },
    '.cm-scroller': {
        overflow: 'auto'
    },
    '.cm-focused': {
        outline: 'none'
    },
    '.cm-gutters': {
        backgroundColor: 'transparent',
        borderRight: '1px solid var(--border, rgba(0,0,0,0.08))'
    }
});

export default function EntityJsonView({
    open,
    onOpenChange,
    title = 'Entity JSON',
    description = null,
    entity,
    sanitize = true,
    onApply = null
}) {
    const editable = typeof onApply === 'function';
    const [copied, setCopied] = useState(false);
    const [mode, setMode] = useState('view'); // 'view' | 'edit'
    const [parseError, setParseError] = useState(null);
    const [errorLine, setErrorLine] = useState(null);

    const hostRef = useRef(null);
    const viewRef = useRef(null);
    const draftRef = useRef(''); // current document text in edit mode

    // Display text for view mode (masked when sanitize=true).
    const viewText = useMemo(
        () => safeStringify(sanitize ? maskSecrets(entity) : entity),
        [entity, sanitize]
    );

    // Raw text for edit mode (always unmasked so the user can edit real values).
    const rawText = useMemo(() => safeStringify(entity), [entity]);

    // Reset to view-mode whenever the dialog reopens.
    useEffect(() => {
        if (open) {
            setMode('view');
            setParseError(null);
            setErrorLine(null);
            draftRef.current = rawText;
        }
    }, [open, rawText]);

    // Mount CodeMirror only when entering edit mode. View mode uses a plain
    // <pre> for reliability — no portal/layout edge cases.
    useEffect(() => {
        if (!open || mode !== 'edit' || !hostRef.current) return undefined;

        // Defensive: clear any stale child nodes from a prior mount (e.g.
        // React StrictMode double-invoke or hot-reload).
        while (hostRef.current.firstChild) {
            hostRef.current.removeChild(hostRef.current.firstChild);
        }

        draftRef.current = rawText;

        const startState = EditorState.create({
            doc: rawText,
            extensions: [
                lineNumbers(),
                foldGutter(),
                codeFolding(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                json(),
                autocompletion(),
                errorLineField,
                errorLineTheme,
                EditorView.lineWrapping,
                EditorView.updateListener.of((update) => {
                    if (!update.docChanged) return;
                    const next = update.state.doc.toString();
                    draftRef.current = next;
                    try {
                        JSON.parse(next);
                        setParseError(null);
                        setErrorLine(null);
                    } catch (err) {
                        const msg = err?.message || 'Invalid JSON';
                        setParseError(msg);
                        const m = /position\s+(\d+)/i.exec(msg);
                        if (m) {
                            const pos = parseInt(m[1], 10);
                            try {
                                const line = update.state.doc.lineAt(pos).number;
                                setErrorLine(line);
                            } catch {
                                setErrorLine(null);
                            }
                        } else {
                            setErrorLine(null);
                        }
                    }
                }),
                editorBaseTheme
            ]
        });

        const cmView = new EditorView({
            state: startState,
            parent: hostRef.current
        });
        viewRef.current = cmView;

        return () => {
            cmView.destroy();
            viewRef.current = null;
        };
    }, [open, mode, rawText]);

    // Reflect parse-error line into the editor via state effect.
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: setErrorLineEffect.of(errorLine)
        });
    }, [errorLine]);

    function handleCopy() {
        const text = mode === 'edit' ? draftRef.current : viewText;
        if (!navigator?.clipboard?.writeText) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }

    function handleApply() {
        let parsed;
        try {
            parsed = JSON.parse(draftRef.current);
        } catch (err) {
            setParseError(err?.message || 'Invalid JSON');
            return;
        }
        try {
            onApply(parsed);
        } catch (err) {
            setParseError(`onApply failed: ${err?.message || String(err)}`);
            return;
        }
        onOpenChange(false);
    }

    function handleResetDraft() {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            changes: {
                from: 0,
                to: view.state.doc.length,
                insert: rawText
            }
        });
        draftRef.current = rawText;
        setParseError(null);
        setErrorLine(null);
    }

    const inEdit = editable && mode === 'edit';
    const effectiveDescription = description
        || (inEdit
            ? 'Edit the JSON. Apply writes the parsed value back into the form (you still need to click Save to persist).'
            : 'Read-only preview of the wire shape that will be saved.');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{effectiveDescription}</DialogDescription>
                        </div>
                        <div className="flex items-center gap-1">
                            {editable && (
                                <DSButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setMode((m) => (m === 'edit' ? 'view' : 'edit'))}
                                    title={inEdit ? 'Switch to read-only view' : 'Edit JSON'}
                                >
                                    {inEdit
                                        ? <><Eye size={13} /> View</>
                                        : <><Pencil size={13} /> Edit</>
                                    }
                                </DSButton>
                            )}
                            <DSButton
                                variant="ghost"
                                size="sm"
                                onClick={handleCopy}
                                title="Copy JSON"
                                disabled={!navigator?.clipboard?.writeText}
                            >
                                {copied
                                    ? <><Check size={13} /> Copied</>
                                    : <><CopyIcon size={13} /> Copy</>
                                }
                            </DSButton>
                        </div>
                    </div>
                </DialogHeader>
                {inEdit ? (
                    <div className={`overflow-hidden rounded-md border ${parseError ? 'border-rose-300' : 'border-muted'} bg-muted/30`}>
                        <div ref={hostRef} />
                    </div>
                ) : (
                    <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-[11.5px]">
                        {viewText}
                    </pre>
                )}
                {inEdit && parseError && (
                    <div className="flex items-start gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-900">
                        <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                        <span className="break-words">{parseError}</span>
                    </div>
                )}
                {!inEdit && sanitize && (
                    <div className="text-[10.5px] text-muted-foreground">
                        Secrets masked. Values shown as <code>{MASK}</code> are redacted from this preview.
                    </div>
                )}
                {inEdit && (
                    <div className="flex items-center justify-end gap-2">
                        <DSButton variant="ghost" size="sm" onClick={handleResetDraft} title="Discard JSON edits">
                            <RotateCcw size={13} /> Reset
                        </DSButton>
                        <DSButton variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                            Cancel
                        </DSButton>
                        <DSButton
                            variant="primary"
                            size="sm"
                            onClick={handleApply}
                            disabled={!!parseError}
                        >
                            Apply
                        </DSButton>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
